import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Select, Space, Table, Tag, message } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const PmvFuelReconciliationPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | undefined>();

  const q = useQuery({
    queryKey: ['pmv-fuel-recon', status],
    queryFn: () => pmvService.listFuelReconciliation({ status, limit: 100 }),
  });

  const items = q.data?.items ?? [];

  const resolve = useMutation({
    mutationFn: (id: string) =>
      pmvService.resolveFuelReconciliation(id, {
        status: 'RESOLVED',
        resolution_notes: 'Accepted within operational review',
      }),
    onSuccess: () => {
      message.success('Reconciliation resolved — Inventory unchanged');
      qc.invalidateQueries({ queryKey: ['pmv-fuel-recon'] });
      qc.invalidateQueries({ queryKey: ['pmv-fuel-control-tower'] });
    },
  });

  const dismiss = useMutation({
    mutationFn: (id: string) =>
      pmvService.dismissFuelReconciliation(id, {
        resolution_notes: 'Observation dismissed with reason',
      }),
    onSuccess: () => {
      message.success('Observation dismissed');
      qc.invalidateQueries({ queryKey: ['pmv-fuel-recon'] });
      qc.invalidateQueries({ queryKey: ['pmv-fuel-control-tower'] });
    },
  });

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Fuel Reconciliation"
        subtitle="Authoritative · Operational · Observed — human review only; no silent stock correction"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/fuel/control-tower')}>Control Tower</Button>
            <Button onClick={() => navigate('/asset/pmv/fuel/attention')}>Attention</Button>
            <Button onClick={() => navigate('/asset/pmv/fuel')}>Fuel Register</Button>
          </Space>
        }
        extra={
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 180 }}
            value={status}
            onChange={setStatus}
            options={[
              'OPEN',
              'VARIANCE',
              'MATCHED',
              'WITHIN_TOLERANCE',
              'RESOLVED',
              'DISMISSED',
            ].map((v) => ({ value: v, label: v }))}
          />
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="warning"
        showIcon
        message="If Inventory quantity is wrong, open Inventory — do not edit stock from this workspace."
      />
      {!q.isLoading && items.length === 0 ? (
        <Empty description="No fuel reconciliation rows for these filters" />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1200 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Recon #',
              dataIndex: 'recon_number',
              width: 140,
              ellipsis: true,
            },
            {
              title: 'Equipment',
              key: 'equipment',
              width: 200,
              ellipsis: true,
              render: (_: unknown, r: Record<string, any>) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}?tab=fuel`)}
                  >
                    {r.equipment_label ||
                      r.fleet_number ||
                      r.asset_code ||
                      r.asset_name ||
                      'Open equipment'}
                  </Button>
                </div>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 130,
              render: (s) => <Tag>{s}</Tag>,
            },
            {
              title: 'Issued (L)',
              dataIndex: 'issued_quantity_litres',
              width: 100,
              render: (v) => (v == null ? '—' : Number(v).toFixed(1)),
            },
            {
              title: 'Operational (L)',
              dataIndex: 'operational_quantity_litres',
              width: 120,
              render: (v) => (v == null ? '—' : Number(v).toFixed(1)),
            },
            {
              title: 'Observed (L)',
              dataIndex: 'observed_quantity_litres',
              width: 110,
              render: (v) => (v == null ? '—' : Number(v).toFixed(1)),
            },
            {
              title: 'Variance',
              dataIndex: 'variance_quantity_litres',
              width: 140,
              render: (v, r) =>
                v == null ? (
                  'Unavailable'
                ) : (
                  <span>
                    {Number(v).toFixed(1)} L
                    {r.variance_percent != null
                      ? ` (${Number(r.variance_percent).toFixed(1)}%)`
                      : ''}
                  </span>
                ),
            },
            { title: 'Quality', dataIndex: 'quality', width: 100 },
            {
              title: 'Actions',
              width: 160,
              render: (_, r) =>
                ['RESOLVED', 'DISMISSED', 'MATCHED', 'WITHIN_TOLERANCE'].includes(r.status) ? (
                  '—'
                ) : (
                  <Space>
                    <Button size="small" onClick={() => resolve.mutate(r.id)}>
                      Resolve
                    </Button>
                    <Button size="small" onClick={() => dismiss.mutate(r.id)}>
                      Dismiss
                    </Button>
                  </Space>
                ),
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvFuelReconciliationPage;
